/**
 * Transaktions-Fingerprint & kanonischer Timestamp.
 *
 * Zweck: Deterministische Dedup über alle Import-Quellen hinweg (CSV, KI, manuell)
 * und sauberer Sort-Schlüssel für FIFO-Berechnung.
 *
 * Felder, die jede TX bekommt:
 *   ts           : ISO-8601 UTC mit ms — kanonischer Zeitstempel
 *   tsPrecision  : 'second' | 'minute' | 'day' — wie genau die Quelle war
 *   tsSource     : 'csv' | 'manual' | 'api' | 'image' | 'chain'
 *   contentHash  : SHA-1-Hex des Tupels (tsBucket | typ | coin | menge | exchange | hash)
 *
 * Wichtig: KEIN automatisches stilles Verwerfen von Duplikaten mehr.
 * Aufrufer markieren collision groups und lassen den User entscheiden.
 */

// ── Zeit-Helfer ──────────────────────────────────────────────────────────────

/**
 * Baut einen kanonischen UTC-Timestamp + Präzision aus { datum, zeit }.
 *
 * - "YYYY-MM-DD" + "HH:mm:ss"    → precision 'second'
 * - "YYYY-MM-DD" + "HH:mm"       → precision 'minute'  (Sekunden :00)
 * - "YYYY-MM-DD" alleine         → precision 'day'     (Mittag UTC, 12:00:00,
 *                                   damit Default nicht mit echten Mitternachts-
 *                                   Trades kollidiert UND später vom Sort
 *                                   noch durch typPrio korrigiert werden kann)
 */
export function buildTs(datum, zeit) {
  if (!datum) return { ts: null, tsPrecision: null };

  if (zeit) {
    const z = String(zeit).trim();
    const hms = z.match(/^(\d{2}):(\d{2}):(\d{2})/);
    if (hms) {
      return {
        ts: `${datum}T${hms[1]}:${hms[2]}:${hms[3]}.000Z`,
        tsPrecision: 'second',
      };
    }
    const hm = z.match(/^(\d{2}):(\d{2})$/);
    if (hm) {
      return {
        ts: `${datum}T${hm[1]}:${hm[2]}:00.000Z`,
        tsPrecision: 'minute',
      };
    }
  }

  return {
    ts: `${datum}T12:00:00.000Z`,
    tsPrecision: 'day',
  };
}

/** Millisekunden-Timestamp aus tx.ts; Fallback auf datum+zeit. */
export function tsMillis(tx) {
  if (tx?.ts) {
    const ms = Date.parse(tx.ts);
    if (!Number.isNaN(ms)) return ms;
  }
  if (tx?.datum) {
    const z = tx.zeit || '00:00:00';
    const ms = Date.parse(`${tx.datum}T${z.length === 5 ? z + ':00' : z}Z`);
    if (!Number.isNaN(ms)) return ms;
  }
  return 0;
}

/** Numerische Reihenfolge: 'second' < 'minute' < 'day' < null. */
export function tsPrecisionRank(p) {
  if (p === 'second') return 0;
  if (p === 'minute') return 1;
  if (p === 'day') return 2;
  return 3;
}

// ── tsBucket: Bucket-Größe je nach Präzision ────────────────────────────────

/**
 * Gibt einen Bucket-String zurück, der nur so genau ist wie die Quelle.
 * Zwei TX mit identischem Bucket + Coin + Menge gelten als kollisionsverdächtig.
 *
 *  precision='second' → 'YYYY-MM-DDTHH:MM:SS'
 *  precision='minute' → 'YYYY-MM-DDTHH:MM'
 *  precision='day'    → 'YYYY-MM-DD'
 */
export function tsBucket(tx) {
  const p = tx?.tsPrecision;
  if (tx?.ts) {
    if (p === 'second') return tx.ts.slice(0, 19);
    if (p === 'minute') return tx.ts.slice(0, 16);
    if (p === 'day')    return tx.ts.slice(0, 10);
    return tx.ts.slice(0, 19);
  }
  const datum = tx?.datum || '';
  const zeit  = tx?.zeit  || '';
  if (!zeit) return datum;
  return `${datum}T${zeit.slice(0, 8)}`;
}

// ── Exchange-Normalisierung ─────────────────────────────────────────────────

/**
 * Vereinheitlicht Exchange-Strings, damit "Binance Spot" und "binance" denselben
 * Hash liefern. Konservativ — nur Whitespace + Case + bekannte Suffixe.
 */
export function normalizeExchange(raw) {
  if (!raw) return '';
  return String(raw)
    .trim()
    .toLowerCase()
    .replace(/\s+(spot|futures|earn|wallet|exchange)$/, '')
    .replace(/\s+/g, '');
}

// ── Hash ────────────────────────────────────────────────────────────────────

/**
 * Quantisiert eine Menge auf 8 Dezimalstellen (Satoshi-Präzision).
 * Verhindert Float-Drift im Hash.
 */
function round8(n) {
  if (n == null || n === '') return 0;
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(Math.abs(v) * 1e8);
}

/** Synchroner SHA-1-ähnlicher Hash (DJB2-Variante, hex, 16 Zeichen).
 *
 * Wir brauchen KEINE kryptographische Sicherheit — nur einen stabilen, kollisions-
 * armen Fingerprint für ~10k TX pro User. Kein crypto.subtle → komplett synchron
 * nutzbar und SSR-freundlich. */
function fastHash(str) {
  // FNV-1a 64-bit, in 32-bit-Hälften gerechnet, hex-konkateniert
  let h1 = 0x811c9dc5 | 0;
  let h2 = 0x1b873593 | 0;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x85ebca6b);
  }
  const hex = (n) => (n >>> 0).toString(16).padStart(8, '0');
  return hex(h1) + hex(h2);
}

/**
 * Erzeugt einen stabilen Inhalts-Hash für eine TX.
 *
 * Regeln:
 *  - Wenn tx.hash (Blockchain-TX-Hash) gesetzt: der dominiert (zwei identische
 *    Beträge mit verschiedenen Chain-Hashes sind GARANTIERT verschiedene TX).
 *  - Sonst: tsBucket + typ + coin/coinOut+coinIn + Mengen + exchange.
 *  - tsBucket nutzt die Präzision der Quelle — sekundengenaue Quellen produzieren
 *    KEINE False-Positive-Dedup bei 18:30:00 vs 18:30:20.
 */
export function contentHash(tx) {
  if (!tx) return '';
  const bucket = tsBucket(tx);
  const typ    = (tx.typ || '').toLowerCase();
  const exch   = normalizeExchange(tx.exchange || tx.platform);
  const chain  = tx.hash ? String(tx.hash).trim().toLowerCase() : '';

  let payload;
  if (typ === 'swap') {
    const cOut = (tx.coinOut || tx.symOut || '').toUpperCase();
    const cIn  = (tx.coinIn  || tx.symIn  || '').toUpperCase();
    payload = `${bucket}|swap|${cOut}>${cIn}|${round8(tx.mengeOut)}|${round8(tx.mengeIn)}|${exch}|${chain}`;
  } else {
    const coin = (tx.coin || tx.sym || '').toUpperCase();
    payload = `${bucket}|${typ}|${coin}|${round8(tx.menge)}|0|${exch}|${chain}`;
  }
  return fastHash(payload);
}

// ── Annotations-Pass ────────────────────────────────────────────────────────

/**
 * Reichert eine TX mit ts/tsPrecision/contentHash an (immutable Copy).
 *
 * @param {object} tx
 * @param {object} [opts]
 * @param {string} [opts.source='csv'] - Wert für tsSource (nur gesetzt wenn nicht schon vorhanden)
 */
export function annotateTx(tx, opts = {}) {
  if (!tx) return tx;
  const out = { ...tx };
  if (!out.ts || !out.tsPrecision) {
    const { ts, tsPrecision } = buildTs(out.datum, out.zeit);
    if (ts && !out.ts) out.ts = ts;
    if (tsPrecision && !out.tsPrecision) out.tsPrecision = tsPrecision;
  }
  if (!out.tsSource && opts.source) out.tsSource = opts.source;
  if (!out.contentHash) out.contentHash = contentHash(out);
  return out;
}

/**
 * Markiert Kollisionsgruppen in einer Batch von TX:
 *   - alle TX mit demselben contentHash bekommen `_collisionGroup` = Hash
 *   - keine TX wird verworfen
 *
 * Aufrufer (Review-Screen) zeigt diese Gruppen side-by-side und lässt User
 * pro TX entscheiden.
 */
export function markCollisions(txs) {
  const groups = new Map();
  for (const tx of txs) {
    const h = tx.contentHash || contentHash(tx);
    if (!groups.has(h)) groups.set(h, []);
    groups.get(h).push(tx);
  }
  return txs.map(tx => {
    const h = tx.contentHash || contentHash(tx);
    const group = groups.get(h);
    if (group && group.length > 1) {
      return { ...tx, contentHash: h, _collisionGroup: h, _collisionSize: group.length };
    }
    return tx.contentHash ? tx : { ...tx, contentHash: h };
  });
}
